// import external libraries
import { useState, useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';

// import hooks
import { queries } from 'RestAPIs/rapini';

// import types
import { Patient } from '../types';
import { PlanningEntityType } from 'generated/index';

// import utils
import { splitCareReceiverName } from '../utils';

export const usePatientSearch = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery] = useDebounceValue(searchQuery, 300);

  const shouldFetch = debouncedQuery.length >= 3;
  const { data: clientsData = [], isLoading } = queries.useFindPlanningEntities(
    debouncedQuery,
    // API expects comma-separated string but type expects array
    ['CLIENT'].join(',') as unknown as PlanningEntityType[],
    100, // max results
    //@ts-expect-error - API expects comma-separated string but type expects array
    ['MRP'].join(','),
    { enabled: shouldFetch } // Only fetch when search query is 3+ characters
  );

  // Transform API data to Patient type
  const patients: Patient[] = useMemo(() => {
    if (!shouldFetch) {
      return [];
    }

    return clientsData
      .filter((client) => !!client.id)
      .map((client) => {
        const { firstName, lastName } = splitCareReceiverName(client.label);
        const clientNumber = client.number?.toString();

        // Create display name for autocomplete
        const nameParts: string[] = [];
        if (lastName) nameParts.push(lastName);
        if (firstName) nameParts.push(firstName);
        const name = nameParts.join(', ');
        const displayName = clientNumber ? `${name} (${clientNumber})` : name;

        return {
          id: client.id!,
          entityId: client.id!,
          name: displayName,
          firstName,
          lastName,
          dateOfBirth: client.birthDate,
          clientNumber
        };
      });
  }, [clientsData, shouldFetch]);

  const handlePatientChange = (
    newValue: Patient | string | null | (Patient | string)[],
    onSelected: (patient: Patient | null) => void
  ) => {
    const patient = Array.isArray(newValue) ? newValue[0] : newValue;
    const selectedValue = typeof patient === 'string' ? null : patient;
    onSelected(selectedValue);
  };

  return {
    searchQuery,
    setSearchQuery,
    patients,
    isLoading,
    handlePatientChange
  };
};
